import React, { FC, useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useSearchParams } from "react-router-dom";
import {
  Stack,
  Typography,
  Tooltip,
  TextField,
  Box,
  alpha,
  useTheme,
  MenuItem,
  Button,
} from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import { DoubleMaterialityESRSAreaEnum } from "@green0meter/api-client";

import { useESRSTopics } from "../../QualitativeAssessment/components/AssessmentEditor/useESRSTopics";

import Table, { TableColumn } from "@components/common/Table";
import {
  DoubleMaterialityResultRow,
  DoubleMaterialityConsolidatedResultRow,
  useGetSurveys,
  useUpdateSurvey,
  useGetResults,
} from "@api/hooks/doubleMateriality";
import { getYearFromSearchParams } from "@helpers";
import { useUserStore } from "@store/user";

export type OverrideKey =
  | "impactLevel"
  | "riskLevel"
  | "opportunityLevel"
  | "externalLevel";
export type OverridesEntry = { [K in OverrideKey]?: number | null };
type OverridesState = Record<string, OverridesEntry>;
export type OverrideInputsState = Record<string, Record<OverrideKey, string>>;
export type DoubleMaterialityResultsOverrides = Record<string, OverridesEntry>;
export type EditingCell = { assessmentId: string; metric: OverrideKey } | null;
export type MaterialityRow =
  | DoubleMaterialityResultRow
  | DoubleMaterialityConsolidatedResultRow;
export type MetricsConfig = {
  key: OverrideKey;
  label: string;
  tooltip: string;
};


// Helper components and functions

const formatNumber = (value: number | null | undefined): string =>
  value == null || Number.isNaN(value) ? "-" : value.toFixed(2);

const getSubTopicDisplayName = (
  topic: MaterialityRow["topic"],
  subTopicCode: string | null | undefined,
  getSubtopicsForTopic: ReturnType<typeof useESRSTopics>["getSubtopicsForTopic"],
): string => {
  if (!subTopicCode || subTopicCode.trim() === "") {
    return "-";
  }
  const subtopics = getSubtopicsForTopic(topic);
  const subtopic = subtopics.find((st) => st.code === subTopicCode);
  const result = subtopic?.name ?? subTopicCode;


  return result && result.trim() !== "" ? result : "-";
};

const getSubSubTopicDisplayName = (
  topic: MaterialityRow["topic"],
  subTopicCode: string | null | undefined,
  subSubTopicCode: string | null | undefined,
  getSubSubtopicsForTopic: ReturnType<
    typeof useESRSTopics
  >["getSubSubtopicsForTopic"],
): string => {
  if (
    !subSubTopicCode ||
    subSubTopicCode.trim() === "" ||
    !subTopicCode ||
    subTopicCode.trim() === ""
  ) {
    return "-";
  }
  const subSubTopics = getSubSubtopicsForTopic(topic, subTopicCode);
  const subSubTopic = subSubTopics.find((sst) => sst.code === subSubTopicCode);
  const result = subSubTopic?.name ?? subSubTopicCode;


  return result && result.trim() !== "" ? result : "-";
};

interface AggregatedResultsTableProps {
  tableRows: MaterialityRow[];
  isTableLoading: boolean;
  isConsolidatedView: boolean;
  thresholds: {
    externalImpact: number;
    internalFinancial: number;
  };
}

export const AggregatedResultsTable: FC<AggregatedResultsTableProps> = ({
  tableRows,
  isTableLoading,
  isConsolidatedView,
  thresholds,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { user } = useUserStore();
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams] = useSearchParams();
  const year = getYearFromSearchParams(searchParams);
  const { getSubtopicsForTopic, getSubSubtopicsForTopic } = useESRSTopics();

  const { surveys } = useGetSurveys(user);
  const { updateSurveyAsync, isPendingUpdateSurvey } = useUpdateSurvey(user);
  const { results, refetchResults, resultsEnabled } = useGetResults(user, {
    year,
  });
  const currentSurvey = surveys?.find((s) => s.year === year);

  const [overridesState, setOverridesState] = useState<OverridesState>({});
  const [overrideInputs, setOverrideInputs] = useState<OverrideInputsState>({});
  const [editingCell, setEditingCell] = useState<EditingCell>(null);
  const [resultsSearch, setResultsSearch] = useState("");
  const [selectedEsrsCodes, setSelectedEsrsCodes] = useState<string[]>([]);
  const [materialityFilter, setMaterialityFilter] = useState<
    "all" | "material" | "notMaterial"
  >("all");

  const metricsConfig: MetricsConfig[] = [
    {
      key: "impactLevel",
      label: t("doubleMateriality.results.impactLevel", "Impact level"),
      tooltip: t(
        "doubleMateriality.results.impactLevelDescription",
        "Average of probability, intensity, range and remediability questions for impact assessments.",
      ),
    },
    {
      key: "riskLevel",
      label: t("doubleMateriality.results.riskLevel", "Risk level"),
      tooltip: t(
        "doubleMateriality.results.riskLevelDescription",
        "Average of probability and severity scores across risk questions.",
      ),
    },
    {
      key: "opportunityLevel",
      label: t("doubleMateriality.results.opportunityLevel", "Opportunity level"),
      tooltip: t(
        "doubleMateriality.results.opportunityLevelDescription",
        "Average of probability and severity scores across opportunity questions.",
      ),
    },
    {
      key: "externalLevel",
      label: t("doubleMateriality.results.externalLevel", "External level"),
      tooltip: t(
        "doubleMateriality.results.externalLevelDescription",
        "Average of external stakeholder responses on a 0-5 scale.",
      ),
    },
  ];

  const ensureRowInputs = (
    row?: Record<OverrideKey, string>,
  ): Record<OverrideKey, string> => ({
    impactLevel: row?.impactLevel ?? "",
    riskLevel: row?.riskLevel ?? "",
    opportunityLevel: row?.opportunityLevel ?? "",
    externalLevel: row?.externalLevel ?? "",
  });

  const applyOverrideValue = (
    source: OverridesState,
    assessmentId: string,
    field: OverrideKey,
    value: number | null,
  ): OverridesState => {
    const next: OverridesState = { ...source };
    const currentEntry: OverridesEntry = next[assessmentId]
      ? { ...next[assessmentId] }
      : {};

    if (value == null) {
      delete currentEntry[field];
    } else {
      currentEntry[field] = value;
    }

    if (Object.keys(currentEntry).length === 0) {
      delete next[assessmentId];
    } else {
      next[assessmentId] = currentEntry;
    }

    return next;
  };

  useEffect(() => {
    const normalizedOverrides: OverridesState = {};

    if (currentSurvey?.resultsOverrides) {
      Object.entries(currentSurvey.resultsOverrides).forEach(
        ([assessmentId, entry]) => {
          if (entry && typeof entry === 'object') {
            normalizedOverrides[assessmentId] = { ...entry } as OverridesEntry;
          }
        },
      );
    }

    setOverridesState(normalizedOverrides);

    const nextInputs: OverrideInputsState = {};

    const rows = (results ?? []) as DoubleMaterialityResultRow[];

    rows.forEach((row) => {
      const rowOverrides = normalizedOverrides[row.assessmentId] ?? {};

      nextInputs[row.assessmentId] = {
        impactLevel:
          rowOverrides.impactLevel != null ? String(rowOverrides.impactLevel) : "",
        riskLevel:
          rowOverrides.riskLevel != null ? String(rowOverrides.riskLevel) : "",
        opportunityLevel:
          rowOverrides.opportunityLevel != null
            ? String(rowOverrides.opportunityLevel)
            : "",
        externalLevel:
          rowOverrides.externalLevel != null
            ? String(rowOverrides.externalLevel)
            : "",
      };
    });

    setOverrideInputs(nextInputs);
  }, [currentSurvey?.id, currentSurvey?.resultsOverrides, results]);

  const handleOverrideInputChange = (
    assessmentId: string,
    field: OverrideKey,
    value: string,
  ): void => {
    setOverrideInputs((prev) => {
      const next = { ...prev };
      const existing = ensureRowInputs(next[assessmentId]);

      next[assessmentId] = { ...existing, [field]: value };

      return next;
    });
  };

  const handleOverrideBlur = async (
    assessmentId: string,
    field: OverrideKey,
  ): Promise<void> => {
    if (!currentSurvey) {
      return;
    }

    const currentInputs = ensureRowInputs(overrideInputs[assessmentId]);
    const rawValue = currentInputs[field]?.trim() ?? "";
    const normalizedString = rawValue.replace(",", ".");
    const hasValue = normalizedString.length > 0;
    const parsedValue = hasValue ? Number(normalizedString) : null;

    if (hasValue && Number.isNaN(parsedValue)) {
      enqueueSnackbar(
        t("doubleMateriality.results.invalidOverride", "Enter a valid number"),
        { variant: "error" },
      );
      setOverrideInputs((prev) => {
        const next = { ...prev };
        const fallback = overridesState[assessmentId]?.[field];
        const existing = ensureRowInputs(next[assessmentId]);

        existing[field] = fallback != null ? String(fallback) : "";
        next[assessmentId] = existing;

        return next;
      });

      return;
    }

    const nextOverrides = applyOverrideValue(
      overridesState,
      assessmentId,
      field,
      parsedValue,
    );

    try {
      const overridesPayload = Object.keys(nextOverrides).length
        ? (nextOverrides as DoubleMaterialityResultsOverrides)
        : null;

      await updateSurveyAsync({
        surveyId: currentSurvey.id,
        params: {
          resultsOverrides: overridesPayload,
        },
      });

      setOverridesState(nextOverrides);
      setOverrideInputs((prev) => {
        const next = { ...prev };
        const existing = ensureRowInputs(next[assessmentId]);

        existing[field] = parsedValue != null ? String(parsedValue) : "";
        next[assessmentId] = existing;

        return next;
      });
      if (resultsEnabled) {
        refetchResults();
      }
      enqueueSnackbar(
        t("doubleMateriality.results.overrideSaved", "Override saved"),
        { variant: "success" },
      );
    } catch {
      enqueueSnackbar(
        t("doubleMateriality.results.overrideSaveError", "Failed to save override"),
        { variant: "error" },
      );
      setOverrideInputs((prev) => {
        const next = { ...prev };
        const fallback = overridesState[assessmentId]?.[field];
        const existing = ensureRowInputs(next[assessmentId]);

        existing[field] = fallback != null ? String(fallback) : "";
        next[assessmentId] = existing;

        return next;
      });
    }
  };

  const resetCellInput = (assessmentId: string, field: OverrideKey): void => {
    const fallback = overridesState[assessmentId]?.[field];

    setOverrideInputs((prev) => {
      const next = { ...prev };
      const existing = ensureRowInputs(next[assessmentId]);

      existing[field] = fallback != null ? String(fallback) : "";
      next[assessmentId] = existing;

      return next;
    });
  };

  const canEditOverrides =
    Boolean(currentSurvey) && !isPendingUpdateSurvey && !isConsolidatedView;

  const calculateIsMaterial = (row: MaterialityRow): { isMaterial: boolean; isRiskMaterial: boolean; isBelowExternal: boolean } => {
    const impactValue = row.impactLevel?.override ?? row.impactLevel?.value ?? 0;
    const riskValue = row.riskLevel?.override ?? row.riskLevel?.value ?? null;
    const riskMagnitude = riskValue != null ? Math.abs(riskValue) : null;
    const opportunityValue =
      row.opportunityLevel?.override ?? row.opportunityLevel?.value ?? null;
    const opportunityMagnitude =
      opportunityValue != null ? Math.abs(opportunityValue) : null;
    const isRiskMaterial =
      riskMagnitude != null && riskMagnitude >= thresholds.internalFinancial;
    const isOpportunityMaterial =
      opportunityMagnitude != null &&
      opportunityMagnitude >= thresholds.internalFinancial;
    const isBelowExternal = impactValue < thresholds.externalImpact;
    const isMaterial = isRiskMaterial || isOpportunityMaterial || !isBelowExternal;


    return { isMaterial, isRiskMaterial, isBelowExternal };
  };

  const rowsWithId = useMemo(() =>
    tableRows.map((row) => ({ ...row, id: row.assessmentId })),
  [tableRows]
  );
  const esrsFilterOptions = useMemo(
    () =>
      Array.from(
        new Set(
          rowsWithId.map((row) =>
            String(DoubleMaterialityESRSAreaEnum[row.topic] ?? row.topic),
          ),
        ),
      ).sort((a, b) => a.localeCompare(b)),
    [rowsWithId],
  );
  const hasResultsFilters =
    resultsSearch.trim().length > 0 ||
    selectedEsrsCodes.length > 0 ||
    materialityFilter !== "all";
  const filteredRowsWithId = useMemo(() => {
    const normalizedSearch = resultsSearch.trim().toLowerCase();

    return rowsWithId.filter((row) => {
      const esrsCode = String(DoubleMaterialityESRSAreaEnum[row.topic] ?? row.topic);

      if (selectedEsrsCodes.length > 0 && !selectedEsrsCodes.includes(esrsCode)) {
        return false;
      }

      if (materialityFilter !== "all") {
        const isMaterial = calculateIsMaterial(row).isMaterial;

        if (materialityFilter === "material" && !isMaterial) return false;
        if (materialityFilter === "notMaterial" && isMaterial) return false;
      }

      if (normalizedSearch) {
        const searchText = [
          row.assessmentFriendlyId,
          row.sustainabilityMatter,
          row.subTopic,
          row.subSubTopic,
          esrsCode,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        if (!searchText.includes(normalizedSearch)) {
          return false;
        }
      }

      return true;
    });
  }, [
    rowsWithId,
    resultsSearch,
    selectedEsrsCodes,
    materialityFilter,
    thresholds.externalImpact,
    thresholds.internalFinancial,
  ]);

  const columns: TableColumn<MaterialityRow & { id: string }>[] = [
    {
      key: "esrs",
      title: t("doubleMateriality.results.esrs", "ESRS"),
      renderCellContent: (row) => {
        const { isMaterial } = calculateIsMaterial(row);

        return (
          <Typography variant="body2" sx={{ fontWeight: isMaterial ? 'bold' : 'inherit' }}>
            {DoubleMaterialityESRSAreaEnum[row.topic] ?? row.topic}
          </Typography>
        );
      },
      tableCellProps: { sx: { width: 100 } }
    },
    {
      key: "sustainabilityMatter",
      title: t("doubleMateriality.results.sustainabilityMatter", "Sustainability matter"),
      renderCellContent: (row) => {
        const subTopicDisplay = getSubTopicDisplayName(
          row.topic,
          row.subTopic,
          getSubtopicsForTopic,
        );
        const subSubTopicDisplay = getSubSubTopicDisplayName(
          row.topic,
          row.subTopic,
          row.subSubTopic,
          getSubSubtopicsForTopic,
        );
        const tooltipContent = (
          <>
            {row.assessmentFriendlyId}
            {subTopicDisplay && subTopicDisplay !== "-" && (
              <>
                <br />
                {"Sub-topic:"} {subTopicDisplay}
              </>
            )}
            {subSubTopicDisplay && subSubTopicDisplay !== "-" && (
              <>
                <br />
                {"Sub-sub-topic:"} {subSubTopicDisplay}
              </>
            )}
          </>
        );

        const { isMaterial } = calculateIsMaterial(row);

        return (
          <Tooltip title={tooltipContent} arrow placement="top">
            <Typography variant="body2" sx={{ fontWeight: isMaterial ? 'bold' : 'inherit' }}>
              {row.sustainabilityMatter}
            </Typography>
          </Tooltip>
        );
      },
      tableCellProps: { sx: { width: 300 } }
    },
    ...metricsConfig.map((metric): TableColumn<MaterialityRow & { id: string }> => ({
      key: metric.key,
      title: (
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          justifyContent="flex-end"
          width="100%"
        >
          <Typography variant="body2">{metric.label}</Typography>
          <Tooltip title={metric.tooltip} arrow placement="top">
            <InfoOutlined
              fontSize="small"
              color="action"
              sx={{ cursor: "pointer" }}
            />
          </Tooltip>
        </Stack>
      ),
      renderCellContent: (row) => {
        const inputState = ensureRowInputs(overrideInputs[row.assessmentId]);
        const metricData = row[metric.key];
        const hasOverride = metricData?.override != null;
        const isEditing =
          editingCell?.assessmentId === row.assessmentId &&
          editingCell?.metric === metric.key;
        const displayValue = formatNumber(metricData?.value);
        const answersCount = metricData?.count;

        const { isMaterial, isRiskMaterial, isBelowExternal } = calculateIsMaterial(row);

        let isMetricGreyedOut = false;

        if (metric.key === "impactLevel") {
          isMetricGreyedOut = isBelowExternal;
        } else if (metric.key === "riskLevel") {
          isMetricGreyedOut = !isRiskMaterial;
        }

        const tooltipTitle = hasOverride
          ? t(
            "doubleMateriality.results.overrideValue",
            "Manual override. Underlying average from {{count}} answer/s: {{average}}",
            {
              count: answersCount,
              average: formatNumber(metricData?.average),
            },
          )
          : t(
            "doubleMateriality.results.averageOfAnswers",
            "average of {{count}} answer/s",
            { count: answersCount },
          );

        const handleKeyDown = (event: React.KeyboardEvent): void => {
          if (event.key === "Enter") {
            event.preventDefault();
            void handleOverrideBlur(row.assessmentId, metric.key).finally(() => setEditingCell(null));
          }
          if (event.key === "Escape") {
            event.preventDefault();
            resetCellInput(row.assessmentId, metric.key);
            setEditingCell(null);
          }
        };

        if (isEditing) {
          return (
            <Box display="flex" justifyContent="flex-end" width="100%">
              <TextField
                size="small"
                autoFocus
                type="number"
                inputProps={{
                  step: 0.01,
                  style: { textAlign: "right" },
                }}
                value={inputState[metric.key]}
                onChange={(event) =>
                  handleOverrideInputChange(
                    row.assessmentId,
                    metric.key,
                    event.target.value,
                  )
                }
                onBlur={() => void handleOverrideBlur(row.assessmentId, metric.key).finally(() => setEditingCell(null))}
                onKeyDown={handleKeyDown}
                sx={{
                  maxWidth: 120,
                  "& .MuiOutlinedInput-root": {
                    height: 32,
                  },
                  "& .MuiOutlinedInput-input": {
                    py: 0.5,
                    fontSize: "0.875rem",
                  },
                }}
              />
            </Box>
          );
        }

        return (
          <Box
            onClick={() => {
              if (!canEditOverrides) return;
              setEditingCell({ assessmentId: row.assessmentId, metric: metric.key });
            }}
            sx={{
              cursor: canEditOverrides ? "pointer" : "default",
              borderRadius: 1,
              border: "1px solid transparent",
              px: 1,
              py: 0.5,
              width: "100%",
              display: "flex",
              justifyContent: "flex-end",
              transition: "border-color 0.2s ease, background-color 0.2s ease",
              "&:hover": canEditOverrides
                ? {
                  borderColor: theme.palette.divider,
                  backgroundColor: alpha(theme.palette.primary.main, 0.04),
                }
                : undefined,
              backgroundColor: isMetricGreyedOut
                ? alpha(theme.palette.action.disabledBackground, 0.1)
                : undefined,
              opacity: isMetricGreyedOut ? 0.45 : 1,
            }}
          >
            <Tooltip title={tooltipTitle} arrow placement="top">
              <Typography variant="body2" sx={{ fontWeight: isMaterial ? 'bold' : 'inherit' }}>
                {displayValue}
              </Typography>
            </Tooltip>
          </Box>
        );

      },
      tableCellProps: { sx: { width: 150 } }
    })),
    ...(isConsolidatedView ? [{
      key: "entities",
      title: t("doubleMateriality.results.entities", "Entities"),
      renderCellContent: (row: MaterialityRow & { id: string }) => {
        const { isMaterial } = calculateIsMaterial(row);
        let content = "";

        if ("entities" in row) {
          content = row.entities.map((entity: { name: string }) => entity.name).join(", ");
        }

        return (
          <Typography variant="body2" sx={{ fontWeight: isMaterial ? 'bold' : 'inherit' }}>
            {content}
          </Typography>
        );
      },
      tableCellProps: { sx: { width: 200 } }
    } as TableColumn<MaterialityRow & { id: string }>] : [])
  ];

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ mb: 2 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", md: "center" }}
        >
          <TextField
            size="small"
            label={t("common.search", "Search")}
            value={resultsSearch}
            onChange={(event) => setResultsSearch(event.target.value)}
            sx={{ minWidth: 220 }}
          />
          <TextField
            select
            size="small"
            label={t("doubleMateriality.results.esrs", "ESRS")}
            value={selectedEsrsCodes}
            onChange={(event) => {
              const value = event.target.value;
              const nextValues = Array.isArray(value) ? value : String(value).split(",");

              setSelectedEsrsCodes(nextValues.map(String));
            }}
            SelectProps={{
              multiple: true,
              renderValue: (selected) => (selected as string[]).join(", "),
            }}
            sx={{ minWidth: 180 }}
          >
            {esrsFilterOptions.map((esrsCode) => (
              <MenuItem key={esrsCode} value={esrsCode}>
                {esrsCode}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label={t("doubleMateriality.results.materiality", "Materiality")}
            value={materialityFilter}
            onChange={(event) =>
              setMaterialityFilter(
                event.target.value as "all" | "material" | "notMaterial",
              )
            }
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="all">
              {t("common.all", "All")}
            </MenuItem>
            <MenuItem value="material">
              {t("doubleMateriality.results.material", "Material")}
            </MenuItem>
            <MenuItem value="notMaterial">
              {t("doubleMateriality.results.notMaterial", "Not material")}
            </MenuItem>
          </TextField>
          {hasResultsFilters && (
            <Button
              variant="text"
              size="small"
              onClick={() => {
                setResultsSearch("");
                setSelectedEsrsCodes([]);
                setMaterialityFilter("all");
              }}
            >
              {t("common.clearFilters", "Clear filters")}
            </Button>
          )}
        </Stack>
      </Box>
      <Table
        data={filteredRowsWithId}
        columns={columns}
        isLoading={isTableLoading}
        dataPlaceholder={
          <Typography color="text.secondary">
            {hasResultsFilters
              ? t(
                "doubleMateriality.results.emptyFiltered",
                "No results match the selected filters.",
              )
              : t("doubleMateriality.results.empty", "No results available yet.")}
          </Typography>
        }
        tableProps={{
          sx: {
            "& .MuiTableCell-root": {
              verticalAlign: "middle", // Align middle to look better with inputs
            }
          }
        }}
      />
    </Box>
  );
};
